import React, { useState } from 'react';
import { 
  Edit3, ChevronDown, ChevronUp, AlertTriangle, 
  Anchor, Users, Wrench, Eye, Radio, Plus 
} from 'lucide-react';

const QuickLogEntriesCard = ({ 
  isExpanded,
  onToggle,
  onAddLogEntry,
  activeVoyage
}) => {
  const [customEntry, setCustomEntry] = useState('');
  
  const quickEntries = [
    {
      icon: Anchor,
      label: 'Altered Course',
      type: 'NAVIGATION',
      category: 'navigation',
      getSummary: () => 'Altered Course',
      getDescription: () => 'Course change for navigation'
    },
    {
      icon: Eye,
      label: 'Visual Check',
      type: 'OBSERVATION',
      category: 'navigation',
      getSummary: () => 'Visual observation',
      getDescription: () => 'Routine visual check of surroundings'
    },
    {
      icon: Radio,
      label: 'Radio Check',
      type: 'COMMUNICATION',
      category: 'communication',
      getSummary: () => 'Radio check',
      getDescription: () => 'Routine radio communication check'
    },
    {
      icon: Wrench,
      label: 'Engine Check',
      type: 'ENGINE',
      category: 'machinery',
      getSummary: () => 'Engine room check',
      getDescription: () => 'Routine engine room inspection'
    },
    {
      icon: Users,
      label: 'Crew Meeting',
      type: 'CREW',
      category: 'crew',
      getSummary: () => 'Crew meeting',
      getDescription: () => 'Safety briefing / crew meeting'
    },
    {
      icon: AlertTriangle,
      label: 'Hazard Observed',
      type: 'HAZARD',
      category: 'safety',
      getSummary: () => 'Hazard observed',
      getDescription: () => 'Potential hazard identified'
    }
  ];

  const handleQuickEntry = (entry) => {
    const logEntry = {
      type: entry.type,
      category: entry.category,
      summary: entry.getSummary(),
      description: entry.getDescription(),
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id
    };
    onAddLogEntry(logEntry);
  };

  const handleCustomEntry = () => {
    if (!customEntry.trim()) return;
    
    const logEntry = {
      type: 'GENERAL',
      category: 'general',
      summary: customEntry,
      description: '',
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id
    };
    onAddLogEntry(logEntry);
    setCustomEntry('');
  };

  return (
    <div className="rounded-lg border-2 bg-white border-gray-200">
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Edit3 className="h-5 w-5 text-gray-600" />
            <div>
              <h4 className="text-sm font-medium">Quick Log Entries</h4>
              <p className="text-xs text-gray-500">Quick buttons for common log entries</p>
            </div>
          </div>
          {isExpanded ? 
            <ChevronUp className="h-5 w-5 text-gray-400" /> : 
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </div>
      </div>
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="mt-4 grid grid-cols-2 gap-2">
            {quickEntries.map((entry, index) => {
              const Icon = entry.icon;
              return (
                <button
                  key={index}
                  onClick={() => handleQuickEntry(entry)}
                  className="flex items-center gap-2 p-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon className="h-4 w-4 text-gray-600" />
                  <span className="text-gray-700">{entry.label}</span>
                </button>
              );
            })}
          </div>
          
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700">Custom Entry</label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                value={customEntry}
                onChange={(e) => setCustomEntry(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCustomEntry()}
                placeholder="Type a quick log entry..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleCustomEntry}
                disabled={!customEntry.trim()}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickLogEntriesCard;